import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import QRCode from "qrcode";
import {
  Lock,
  LogOut,
  Plus,
  Trash2,
  Save,
  Package,
  X,
  CheckCircle2,
  Circle,
  RefreshCw,
  Printer,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "spedyfly_admin_token";
const STATUSES = ["In lavorazione", "In transito", "In consegna", "Consegnato"];

// Convert stored timestamp (ISO or naive) to datetime-local input value (YYYY-MM-DDTHH:mm)
const toInputDT = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};


const emptyShipment = () => ({
  code: "",
  status: "In lavorazione",
  sender: "",
  recipient: "",
  origin: "",
  destination: "",
  departure_date: "",
  expected_delivery: "",
  packages: [],
  steps: [],
});

const Field = ({ label, value, onChange, placeholder, type = "text" }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wide text-[#9a9aa5]">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-[#16182C] outline-none focus:border-[#E23028] transition-colors"
    />
  </label>
);

const Login = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (e) {
      setError(e?.response?.data?.detail || "Accesso non riuscito");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
        <div className="w-14 h-14 rounded-2xl bg-[#F6E8E8] text-[#E23028] flex items-center justify-center mx-auto">
          <Lock size={26} />
        </div>
        <h1 className="text-center heading-navy font-bold text-2xl mt-4">Area Amministratore</h1>
        <p className="text-center text-sm text-[#7a7a85] mt-1">Accedi per gestire le spedizioni</p>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Password"
          className="mt-6 w-full rounded-md border border-gray-300 px-4 py-3 outline-none focus:border-[#E23028] transition-colors"
        />
        {error && <p className="text-[#E23028] text-sm mt-2">{error}</p>}
        <button
          onClick={submit}
          disabled={loading}
          className="mt-5 w-full bg-[#E23028] hover:bg-[#c31f18] disabled:opacity-70 text-white font-semibold py-3 rounded-md transition-colors"
        >
          {loading ? "Accesso..." : "Accedi"}
        </button>
      </div>
    </div>
  );
};

const StepEditor = ({ steps, setSteps }) => {
  const update = (i, key, val) =>
    setSteps(steps.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)));
  const toggleDone = (i) =>
    setSteps(
      steps.map((s, idx) => {
        if (idx !== i) return s;
        const done = !s.done;
        // Auto-set timestamp when marking done if not already set
        const timestamp = done && !s.timestamp ? new Date().toISOString() : s.timestamp;
        return { ...s, done, timestamp };
      })
    );
  const remove = (i) => setSteps(steps.filter((_, idx) => idx !== i));
  const add = () => setSteps([...steps, { label: "", place: "", done: false, timestamp: null }]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#9a9aa5]">
          Stati di consegna
        </span>
        <button onClick={add} className="text-[#E23028] text-sm font-semibold inline-flex items-center gap-1">
          <Plus size={15} /> Aggiungi
        </button>
      </div>
      <div className="mt-2 space-y-2">
        {steps.length === 0 && (
          <p className="text-sm text-gray-400">Nessuno step. Aggiungine uno.</p>
        )}
        {steps.map((s, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2 bg-gray-50 rounded-md p-2">
            <button
              onClick={() => toggleDone(i)}
              title={s.done ? "Completato" : "Da completare"}
              className={s.done ? "text-[#3ecf6e]" : "text-gray-400"}
            >
              {s.done ? <CheckCircle2 size={20} /> : <Circle size={20} />}
            </button>
            <input
              value={s.label}
              onChange={(e) => update(i, "label", e.target.value)}
              placeholder="Stato (es. In transito)"
              className="flex-1 min-w-[140px] rounded border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-[#E23028]"
            />
            <input
              value={s.place}
              onChange={(e) => update(i, "place", e.target.value)}
              placeholder="Luogo"
              className="w-32 rounded border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-[#E23028]"
            />
            <input
              type="datetime-local"
              value={toInputDT(s.timestamp)}
              onChange={(e) => update(i, "timestamp", e.target.value ? new Date(e.target.value).toISOString() : null)}
              title="Data e ora"
              className="w-48 rounded border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-[#E23028]"
            />
            <button onClick={() => remove(i)} className="text-gray-400 hover:text-[#E23028]">
              <X size={18} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const PackageEditor = ({ packages, setPackages }) => {
  const update = (i, val) =>
    setPackages(packages.map((p, idx) => (idx === i ? { ...p, weight: val } : p)));
  const remove = (i) => setPackages(packages.filter((_, idx) => idx !== i));
  const add = () => setPackages([...packages, { weight: "" }]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#9a9aa5]">
          Colli ({packages.length})
        </span>
        <button onClick={add} className="text-[#E23028] text-sm font-semibold inline-flex items-center gap-1">
          <Plus size={15} /> Aggiungi collo
        </button>
      </div>
      <div className="mt-2 space-y-2">
        {packages.length === 0 && (
          <p className="text-sm text-gray-400">Nessun collo. Aggiungine uno.</p>
        )}
        {packages.map((p, i) => (
          <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-md p-2">
            <span className="text-sm font-semibold text-[#9a9aa5] w-16">Collo {i + 1}</span>
            <input
              value={p.weight}
              onChange={(e) => update(i, e.target.value)}
              placeholder="Peso (es. 2.5 kg)"
              className="flex-1 min-w-0 rounded border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-[#E23028]"
            />
            <button onClick={() => remove(i)} className="text-gray-400 hover:text-[#E23028]">
              <X size={18} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};


const ShipmentForm = ({ token, initial, onSaved, onCancel }) => {
  const [form, setForm] = useState(initial || emptyShipment());
  const [steps, setSteps] = useState(initial?.steps || []);
  const [packages, setPackages] = useState(initial?.packages || []);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const headers = { Authorization: `Bearer ${token}` };
  const isEdit = Boolean(initial?.id);

  const save = async () => {
    setError("");
    if (!form.code.trim()) {
      setError("Il codice spedizione \u00e8 obbligatorio");
      return;
    }
    setSaving(true);
    const payload = { ...form, steps, packages };
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/shipments/${initial.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/admin/shipments`, payload, { headers });
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="heading-navy font-bold text-xl">
          {isEdit ? "Modifica spedizione" : "Nuova spedizione"}
        </h2>
        <button onClick={onCancel} className="text-gray-400 hover:text-[#E23028]">
          <X size={22} />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Codice (SF-...)" value={form.code} onChange={set("code")} placeholder="SF-12345678" />
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wide text-[#9a9aa5]">Stato</span>
          <select
            value={form.status}
            onChange={(e) => set("status")(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-[#16182C] outline-none focus:border-[#E23028] bg-white"
          >
            {STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </label>
        <Field label="Mittente" value={form.sender} onChange={set("sender")} placeholder="Spedy Fly S.r.l." />
        <Field label="Destinatario" value={form.recipient} onChange={set("recipient")} placeholder="Mario Rossi" />
        <Field label="Origine" value={form.origin} onChange={set("origin")} placeholder="Milano (MI)" />
        <Field label="Destinazione" value={form.destination} onChange={set("destination")} placeholder="Roma (RM)" />
        <Field label="Data di partenza" type="date" value={form.departure_date} onChange={set("departure_date")} />
        <Field label="Data prevista di consegna" type="date" value={form.expected_delivery} onChange={set("expected_delivery")} />
      </div>
      <div className="mt-5">
        <PackageEditor packages={packages} setPackages={setPackages} />
      </div>
      <div className="mt-5">
        <StepEditor steps={steps} setSteps={setSteps} />
      </div>
      {error && <p className="text-[#E23028] text-sm mt-3">{error}</p>}
      <div className="mt-6 flex gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-[#E23028] hover:bg-[#c31f18] disabled:opacity-70 text-white font-semibold px-6 py-2.5 rounded-md transition-colors"
        >
          <Save size={18} /> {saving ? "Salvataggio..." : "Salva"}
        </button>
        <button onClick={onCancel} className="px-6 py-2.5 rounded-md border border-gray-300 text-[#4a4a55] font-medium hover:bg-gray-50">
          Annulla
        </button>
      </div>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // shipment object or 'new'

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/shipments`, { headers });
      setShipments(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  const remove = async (sid) => {
    if (!window.confirm("Eliminare questa spedizione?")) return;
    try {
      await axios.delete(`${API}/admin/shipments/${sid}`, { headers });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  const printLabel = async (s) => {
    const esc = (v) =>
      String(v || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    let qr = "";
    try {
      qr = await QRCode.toDataURL(s.code, { width: 220, margin: 1 });
    } catch (e) {
      qr = "";
    }
    const colli = (s.packages || [])
      .map((p, i) => `<tr><td>Collo ${i + 1}</td><td>${esc(p.weight || "n/d")}</td></tr>`)
      .join("");
    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>Etichetta ${esc(s.code)}</title>
      <style>
        * { box-sizing: border-box; font-family: Arial, Helvetica, sans-serif; }
        body { margin: 0; padding: 24px; color: #16182C; }
        .label { width: 620px; max-width: 100%; margin: 0 auto; border: 2px solid #16182C; border-radius: 10px; overflow: hidden; }
        .head { display:flex; justify-content:space-between; align-items:center; background:#E23028; color:#fff; padding:14px 18px; }
        .head .brand { font-size: 22px; font-weight: 800; }
        .head .tag { font-size: 11px; letter-spacing: 3px; opacity:.9; }
        .body { display:flex; gap:18px; padding:18px; }
        .info { flex:1; }
        .code { font-size: 26px; font-weight: 800; letter-spacing: 1px; }
        .status { display:inline-block; margin-top:6px; font-size:12px; font-weight:700; color:#E23028; background:#F6E8E8; padding:3px 10px; border-radius:999px; }
        .row { margin-top:10px; font-size:13px; }
        .row .k { text-transform:uppercase; font-size:10px; color:#8a8a95; letter-spacing:1px; }
        .row .v { font-weight:600; }
        .qr { text-align:center; }
        .qr img { width:180px; height:180px; }
        table { width:100%; border-collapse:collapse; margin-top:10px; font-size:12px; }
        td { border-top:1px solid #eee; padding:4px 0; }
        td:last-child { text-align:right; font-weight:600; }
        .foot { border-top:1px dashed #ccc; padding:10px 18px; font-size:11px; color:#8a8a95; text-align:center; }
      </style></head>
      <body>
        <div class="label">
          <div class="head"><div><div class="brand">Spedy Fly</div><div class="tag">CORRIERE EXPRESSO</div></div><div class="tag">ETICHETTA SPEDIZIONE</div></div>
          <div class="body">
            <div class="info">
              <div class="code">${esc(s.code)}</div>
              <span class="status">${esc(s.status)}</span>
              <div class="row"><div class="k">Da</div><div class="v">${esc(s.sender || "-")} &middot; ${esc(s.origin || "-")}</div></div>
              <div class="row"><div class="k">A</div><div class="v">${esc(s.recipient || "-")} &middot; ${esc(s.destination || "-")}</div></div>
              <div class="row"><div class="k">Partenza / Consegna prevista</div><div class="v">${esc(s.departure_date || "-")} &nbsp;&rarr;&nbsp; ${esc(s.expected_delivery || "-")}</div></div>
              <table><tbody>${colli || '<tr><td>Colli</td><td>0</td></tr>'}</tbody></table>
            </div>
            <div class="qr">${qr ? `<img src="${qr}" alt="QR ${esc(s.code)}"/>` : ""}<div style="font-size:11px;color:#8a8a95">Scansiona per tracciare</div></div>
          </div>
          <div class="foot">spedyfly.com &middot; Traccia la tua spedizione con il codice ${esc(s.code)}</div>
        </div>
        <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };<\/script>
      </body></html>`;
    const w = window.open("", "_blank", "width=720,height=680");
    if (!w) {
      alert("Consenti i popup per stampare l'etichetta.");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  if (!token) return <Login onLogin={setToken} />;

  return (
    <section className="bg-[#f7f5f5] min-h-[80vh] py-10">
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#E23028] text-white flex items-center justify-center">
              <Package size={22} />
            </div>
            <h1 className="heading-navy font-bold text-2xl">Gestione Spedizioni</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="inline-flex items-center gap-2 border border-gray-300 bg-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-50">
              <RefreshCw size={16} /> Aggiorna
            </button>
            <button onClick={() => setEditing("new")} className="inline-flex items-center gap-2 bg-[#E23028] hover:bg-[#c31f18] text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors">
              <Plus size={16} /> Nuova
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 border border-gray-300 bg-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-50">
              <LogOut size={16} /> Esci
            </button>
          </div>
        </div>

        {editing && (
          <div className="mt-6">
            <ShipmentForm
              token={token}
              initial={editing === "new" ? null : editing}
              onCancel={() => setEditing(null)}
              onSaved={() => {
                setEditing(null);
                load();
              }}
            />
          </div>
        )}

        <div className="mt-6 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <p className="p-8 text-center text-gray-400">Caricamento...</p>
          ) : shipments.length === 0 ? (
            <p className="p-8 text-center text-gray-400">Nessuna spedizione. Creane una nuova.</p>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-[#9a9aa5]">
                <tr>
                  <th className="px-5 py-3">Codice</th>
                  <th className="px-5 py-3">Stato</th>
                  <th className="px-5 py-3 hidden md:table-cell">Destinatario</th>
                  <th className="px-5 py-3 hidden md:table-cell">Tratta</th>
                  <th className="px-5 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shipments.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-semibold text-[#16182C]">{s.code}</td>
                    <td className="px-5 py-3">
                      <span className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full bg-[#F6E8E8] text-[#E23028]">
                        {s.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell text-[#4a4a55]">{s.recipient || "-"}</td>
                    <td className="px-5 py-3 hidden md:table-cell text-[#7a7a85] text-sm">
                      {s.origin || "?"} &rarr; {s.destination || "?"}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-3 items-center">
                        <button onClick={() => printLabel(s)} className="text-sm font-semibold text-[#16182C] hover:text-[#E23028] inline-flex items-center gap-1" title="Stampa etichetta con QR">
                          <Printer size={16} /> Etichetta
                        </button>
                        <button onClick={() => setEditing(s)} className="text-sm font-semibold text-[#E23028] hover:underline">
                          Modifica
                        </button>
                        <button onClick={() => remove(s.id)} className="text-gray-400 hover:text-[#E23028]">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
};

export default Admin;
